import { apiService } from '@/services/api-service';
import type { AccountFlag, PrintMode } from '@/types/models';
import { formatBusinessDateIso } from '@/utils/date-utils';
import { type DuesCadence, type DuesScheduleRow, nextDuesPeriodStart, nextPortfolioDuesDate } from '@/utils/dues-period';
import { formatMoneyCents } from '@/utils/format-money';

/**
 * Shared types + transport for the Post Dues / Post Late Fees flows. Used by
 * both the legacy modal (`post-cron-button.tsx`, still used for the
 * portfolio-wide "ALL" scope) and the per-association full pages
 * (`post-cron-page.tsx`). Keeps the idempotent chunked-commit contract and the
 * preview row shape single-sourced so the two surfaces never drift.
 */

export type CronAction = 'post-dues' | 'post-late-fees';

/** One overdue category in a late-fee preview row (Dues / Water / Other). */
export type OverdueBucket = { bucket: string; cents: number; triggers: boolean; invoiceIds?: string[]; chargeIds?: string[] };

export type CronPreviewRow = {
	accountId: string;
	accountNumber: string | null;
	address: string | null;
	primaryOwnerName: string | null;
	flags: AccountFlag[];
	dueCents: number;
	currentBalanceCents: number;
	creditAvailableCents: number;
	willPost: boolean;
	skipReason?: 'already_posted' | 'zero_due' | 'cap_reached' | 'fee_zero'
		| 'no_rules' | 'deselected' | 'paused' | 'credit_covers' | 'below_water_threshold'
		| 'no_triggering_charge' | 'split_changed';
	/** `split_changed` only: what this period's existing charges total, set against `dueCents`. */
	postedCents?: number;
	/** This post COMPLETES a partial earlier posting: `dueCents` is only the missing lines. */
	resumesPartial?: boolean;
	/** The original invoice the resume lands on; absent when a new invoice is made instead. */
	resumeInvoiceNumber?: string;
	/** Resume only: this period's existing charges dated in the future — not in today's balance. */
	pendingPeriodCents?: number;
	// Late-fee specific (undefined for dues):
	overdue?: OverdueBucket[];
	overdueTriggeringCents?: number;
	overdueExemptCents?: number;
	feeCents?: number;
	legalAdminCents?: number;
	/** Account balance computed AS OF the post date (excludes later payments). The
	 *  late-fee report's OVERDUE column + the New Balance base. */
	balanceAsOfPostDateCents?: number;
	/** Unpaid water (exempt-GL) balance as of the post date — shown in the Water
	 *  column for associations that bill water. */
	waterBalanceAsOfPostDateCents?: number;
	/** Amount of the account's most recent water bill posted within 30 days of the
	 *  post date (0 if none) — the late-fee threshold; surfaced for the
	 *  'below_water_threshold' skip. */
	mostRecentWaterBillCents?: number;
};

export interface PreviewPayload {
	posted: number;
	skipped: number;
	dryRun: boolean;
	details?: CronPreviewRow[];
	invoiceIds?: string[];
	importBatchId?: string;
	/** Late-fee grace days (longest active rule); drives the auto post-date. */
	graceDays?: number | null;
	/** True when the association bills water (has ≥1 exempt-GL charge) — gates the
	 *  Water column in the late-fee report. */
	waterBilled?: boolean;
	/** The invoice number the first created invoice will use for this association
	 *  (honoring any override floor). Present only for per-association runs. */
	nextInvoiceNumber?: string;
	/** Real dues runs: earlier postings of the period whose unfinished side effects the run
	 *  completed — including accounts it was not asked to post. */
	repaired?: number;
	/** Real dues runs: postings the repair could NOT finish, each with why. */
	unfinished?: { sourceRef: string; reason: string }[];
	/** Dues previews: postings of the period an interrupted run left unfinished. */
	unfinishedCount?: number;
}

/**
 * The repair half of a Post Dues result, for the "Done" message: what was finished, and what
 * could not be with the first reason — a person has to act on that one (e.g. reopen a month).
 */
export const repairSummaryText = (repaired: number, unfinished: { reason: string }[]): string =>
	(repaired > 0 ? ` Repaired ${repaired} earlier posting${repaired === 1 ? '' : 's'}.` : '')
	+ (unfinished.length > 0
		? ` ${unfinished.length} posting${unfinished.length === 1 ? '' : 's'} could not be finished: ${unfinished[0].reason}`
		: '');

// Accounts posted per chunked POST. Each account does ~10–15 D1 round-trips
// (invoice + N charges + recompute + audit), so keep this well under the
// Worker's ~1000 subrequest-per-invocation cap. 25 ≈ 375 round-trips/chunk.
export const POST_CHUNK_SIZE = 25;

export const actionNoun = (action: CronAction): string =>
	action === 'post-dues' ? 'dues' : 'late fees';

export const accountLabel = (r: CronPreviewRow): string => {
	const namePart = r.primaryOwnerName ?? r.address ?? '';
	return r.accountNumber ? (namePart ? `${r.accountNumber} · ${namePart}` : r.accountNumber) : namePart;
};

/**
 * A row that cannot charge this run whatever the PM does — for dues, only an account with no
 * dues configured (`zero_due`). Everything else (`already_posted`, `paused`, `deselected`, …)
 * is something a PM needs to SEE, even unchecked: hiding `already_posted` is how a 2026-07-01
 * run skipped two accounts with no explanation on screen (#333).
 *
 * Late fees have no never-charge class here: their default view stays the will-post filter,
 * and they open on "Showing all" anyway (#304).
 */
export const isNeverCharge = (r: CronPreviewRow, action: CronAction): boolean =>
	action === 'post-dues' && r.skipReason === 'zero_due';

/**
 * Status text for a `split_changed` row: part of the period was posted under a split that has
 * since changed, so finishing it at today's amounts would not total the dues. Nothing posts;
 * the PM reviews the existing charges.
 */
export const splitChangedText = (r: CronPreviewRow): string =>
	`Split changed — existing charges total ${formatMoneyCents(r.postedCents ?? 0)} for this period `
	+ `but the current split expects ${formatMoneyCents(r.dueCents)}. Review before posting.`;

/**
 * Status text for a row that completes a partial earlier posting (an interrupted run). It adds
 * only the missing lines — `dueCents` — and, while the original invoice exists, adds them to it
 * rather than numbering a new one.
 */
export const resumeText = (r: CronPreviewRow): string =>
	`Completing a partial posting — adds ${formatMoneyCents(r.dueCents)} `
	+ (r.resumeInvoiceNumber ? `to invoice #${r.resumeInvoiceNumber}` : 'on a new invoice');

/**
 * A dues row's New Balance: today's balance, plus — when it will be charged — the period's
 * already-posted charges dated in the future (a resume of an advance posting; today's balance
 * leaves them out) and what this run adds. For a resume that totals exactly what a fresh
 * posting of the whole period would show.
 */
export const duesNewBalanceCents = (r: CronPreviewRow, charged: boolean): number =>
	r.currentBalanceCents + (charged ? (r.pendingPeriodCents ?? 0) + r.dueCents : 0);

/** The rows the Post page shows before the PM toggles "Showing all". */
export const defaultVisibleRows = (
	rows: CronPreviewRow[],
	action: CronAction,
	selectedIds: ReadonlySet<string>,
): CronPreviewRow[] => action === 'post-dues'
	? rows.filter((r) => !isNeverCharge(r, action))
	: rows.filter((r) => selectedIds.has(r.accountId));

// --- Date helpers (Eastern Time, matching the cron's posting-date semantics) ---

const ET_FMT = new Intl.DateTimeFormat('en-US', {
	timeZone: 'America/New_York', year: 'numeric', month: '2-digit', day: '2-digit',
});

export const todayInEt = (): string => {
	const parts = ET_FMT.formatToParts(new Date());
	const get = (t: string) => parts.find((p) => p.type === t)!.value;
	return `${get('year')}-${get('month')}-${get('day')}`;
};

export const monthFromDate = (ymd: string): string => ymd.slice(0, 7);

/**
 * Post Dues' default post date AND period: the association's next dues date in ET (see
 * `nextDuesPeriodStart`), so an annual association opened in October posts 2027-01-01 for 2027.
 */
export const defaultDuesPosting = (
	cadence: DuesCadence,
	duesDay: number | null | undefined,
): { postingDate: string; forPeriod: string } => {
	const postingDate = formatBusinessDateIso(nextDuesPeriodStart(cadence, duesDay));
	return { postingDate, forPeriod: monthFromDate(postingDate) };
};

/**
 * The portfolio-wide default: the earliest next dues date across `rows` (see
 * `nextPortfolioDuesDate`); next month's 1st when no association has a dues day.
 */
export const defaultPortfolioDuesPosting = (rows: DuesScheduleRow[]): { postingDate: string; forPeriod: string } => {
	const earliest = nextPortfolioDuesDate(rows);
	if (earliest === null) return defaultDuesPosting('monthly', 1);
	const postingDate = formatBusinessDateIso(earliest);
	return { postingDate, forPeriod: monthFromDate(postingDate) };
};

export const ymdFromDate = (d: Date | null): string => {
	if (!d) return todayInEt();
	const parts = ET_FMT.formatToParts(d);
	const get = (t: string) => parts.find((p) => p.type === t)!.value;
	return `${get('year')}-${get('month')}-${get('day')}`;
};

export const dateFromYmd = (s: string): Date => {
	const [y, m, d] = s.split('-').map(Number);
	return new Date(Date.UTC(y, m - 1, d, 12, 0, 0));
};

interface TriggerBody {
	associationId?: string;
	dryRun?: boolean;
	postingDate?: string;
	forPeriod?: string;
	selectedAccountIds?: string[];
	printMode?: PrintMode;
	/** Numeric string; overrides the starting invoice number (floor). Per-association only. */
	startInvoiceNumber?: string;
	/** Per-account explicit invoice numbers (accountId → numeric string) from the
	 *  inline Invoice # column. Wins over startInvoiceNumber per account. */
	invoiceNumbers?: Record<string, string>;
	importBatchId?: string;
	finalize?: boolean;
	/** Dues: finish the period's interrupted postings only; with an EMPTY selection. */
	repairOnly?: boolean;
}

export const triggerCron = async (
	action: CronAction,
	body: TriggerBody,
	timeoutMs?: number,
	signal?: AbortSignal,
): Promise<PreviewPayload> =>
	apiService.fetchApi(`/accounting/${action}`, {
		method: 'POST',
		headers: { 'Content-Type': 'application/json' },
		body: JSON.stringify(body),
		...(timeoutMs ? { timeoutMs } : {}),
		...(signal ? { signal } : {}),
	});

/**
 * Run the real post as a sequence of short, idempotent chunked POSTs (issue
 * #231). The server reuses one import batch across chunks; INSERT OR IGNORE on
 * the period-keyed source_ref makes every chunk safe to re-drive on retry.
 */
export const runChunkedCommit = async (params: {
	action: CronAction;
	associationId?: string;
	postingDate: string;
	forPeriod: string;
	printMode?: PrintMode;
	startInvoiceNumber?: string;
	invoiceNumbers?: Record<string, string>;
	selectedAccountIds: string[];
	onProgress?: (done: number, total: number) => void;
}): Promise<{
	posted: number; skipped: number; repaired: number; unfinished: { sourceRef: string; reason: string }[];
	invoiceIds: string[];
}> => {
	const { action, associationId, postingDate, forPeriod, printMode, startInvoiceNumber, invoiceNumbers, selectedAccountIds, onProgress } = params;
	const chunks: string[][] = [];
	for (let i = 0; i < selectedAccountIds.length; i += POST_CHUNK_SIZE) {
		chunks.push(selectedAccountIds.slice(i, i + POST_CHUNK_SIZE));
	}
	let importBatchId: string | undefined;
	let posted = 0;
	let skipped = 0;
	let repaired = 0;
	const unfinished: { sourceRef: string; reason: string }[] = [];
	const invoiceIds: string[] = [];
	onProgress?.(0, selectedAccountIds.length);
	for (let i = 0; i < chunks.length; i++) {
		// Only send this chunk's slice of the per-account invoice numbers. A cleared cell ('') is
		// left out — the account takes the next sequential number — since the route refuses a blank.
		const chunkInvNums = invoiceNumbers
			? Object.fromEntries(chunks[i].filter((a) => !!invoiceNumbers[a]).map((a) => [a, invoiceNumbers[a]]))
			: undefined;
		const result = await triggerCron(action, {
			associationId, postingDate, forPeriod,
			selectedAccountIds: chunks[i],
			printMode: action === 'post-dues' ? printMode : undefined,
			// Floor semantics make this safe to send on every chunk: once numbers
			// pass the override, it's a no-op and numbering continues sequentially.
			startInvoiceNumber,
			invoiceNumbers: chunkInvNums && Object.keys(chunkInvNums).length > 0 ? chunkInvNums : undefined,
			importBatchId,
			finalize: i === chunks.length - 1,
		}, 90_000);
		importBatchId = result.importBatchId ?? importBatchId;
		posted += result.posted;
		skipped += result.skipped;
		// Only the last chunk (finalize) runs the repair pass, so these are not counted twice.
		repaired += result.repaired ?? 0;
		unfinished.push(...(result.unfinished ?? []));
		invoiceIds.push(...(result.invoiceIds ?? []));
		onProgress?.(Math.min(selectedAccountIds.length, (i + 1) * POST_CHUNK_SIZE), selectedAccountIds.length);
	}
	return { posted, skipped, repaired, unfinished, invoiceIds };
};
